import { memo } from 'react'
import { useImageErrorHandler } from '../hooks/useImageErrorHandler'
import { getCategoryIcon, getCategoryColor, getCategoryIconColor } from '../utils/entityImageUtils'

/**
 * ImageWithFallback - Standardized image component with automatic fallback handling
 * 
 * Features:
 * - Automatic error handling with category icon fallback
 * - Lazy loading support
 * - CDN URL normalization
 * - Loading states
 * - Responsive classes
 * 
 * Consolidates patterns from OptimizedImage, EntityImage, and manual error handlers
 * Following AIM principles (<300 lines, idempotent with React.memo())
 * 
 * @param {string} src - Image URL (supports relative URLs, auto-converts to CDN)
 * @param {string} alt - Alt text
 * @param {string} className - Image className
 * @param {string} category - Entity category for fallback icon
 * @param {string} entityName - Entity name for fallback display
 * @param {boolean} loading - Enable lazy loading (default: true)
 * @param {Function} onClick - Click handler
 * @param {Function} onError - Custom error handler
 * @param {boolean} showIconFallback - Show category icon on error (default: true)
 * @param {string} iconClassName - Fallback icon className
 * @param {string} containerClassName - Container className for fallback
 */
const ImageWithFallback = memo(function ImageWithFallback({
  src,
  alt = '',
  className = 'w-full h-full object-cover',
  category = null,
  entityName = null,
  loading = true,
  onClick,
  onError: customOnError,
  showIconFallback = true,
  iconClassName = 'w-24 h-24',
  containerClassName = '',
  style = {}
}) {
  // Normalize URL (handle relative paths for local dev)
  const normalizedSrc = src?.startsWith('http') 
    ? src 
    : src ? `http://localhost:8081${src}` : null
  
  // Get fallback icon components
  const IconComponent = showIconFallback && category ? getCategoryIcon(category) : null
  const bgColor = getCategoryColor(category)
  const iconColor = getCategoryIconColor(category)
  
  // Use standard error handler
  const handleImageError = useImageErrorHandler({
    onError: customOnError,
    hideOnError: true,
    showFallback: showIconFallback
  })
  
  // If no source, show fallback immediately
  if (!normalizedSrc && showIconFallback && IconComponent) {
    return (
      <div className={`${bgColor} flex items-center justify-center ${containerClassName}`}>
        <IconComponent className={`${iconClassName} ${iconColor}`} />
      </div>
    )
  }
  
  return (
    <>
      <img
        src={normalizedSrc}
        alt={alt || entityName || 'Image'}
        className={className}
        style={style}
        loading={loading ? 'lazy' : 'eager'}
        onClick={onClick}
        onError={handleImageError}
      />
      {/* Fallback icon (hidden until error) */}
      {showIconFallback && IconComponent && (
        <div 
          className={`hidden ${bgColor} flex items-center justify-center ${containerClassName}`}
        >
          <IconComponent className={`${iconClassName} ${iconColor}`} />
        </div>
      )}
    </>
  )
})

export default ImageWithFallback
